import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Modal, Button } from '../../../components/common';
import CmsField from '../../cms/components/CmsField';
import { invoiceService } from '../../../services/invoiceService';
import { getApiError, getApiFieldErrors } from '../../../services/cmsService';
import { PAYMENT_MODES, formatINR, todayISO } from '../billingUtils';

/**
 * Record a receipt against an issued invoice. `onRecorded(invoice)` receives
 * the updated invoice — callers replace their state with it.
 */
const RecordPaymentModal = ({ isOpen, onClose, invoice, onRecorded }) => {
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !invoice) return;
    setForm({
      amount: invoice.balanceAmount ?? '',
      paymentDate: todayISO(),
      mode: 'bank_transfer',
      reference: '',
      notes: '',
    });
    setErrors({});
  }, [isOpen, invoice]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSave = async () => {
    const next = {};
    if (!String(form.amount).trim()) next.amount = 'Enter the amount received';
    if (!form.paymentDate) next.paymentDate = 'Enter the payment date';
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }

    setSaving(true);
    try {
      const updated = await invoiceService.recordPayment(invoice.id, form);
      toast.success(`Payment of ${formatINR(form.amount)} recorded`);
      onRecorded?.(updated);
      onClose();
    } catch (error) {
      const api = getApiFieldErrors(error);
      setErrors({
        amount: api.amount || api.invoice,
        paymentDate: api.payment_date,
        mode: api.mode,
        reference: api.reference,
        notes: api.notes,
      });
      toast.error(getApiError(error, 'Failed to record the payment'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Record payment${invoice?.number ? ` — ${invoice.number}` : ''}`}
      size="medium"
      closeOnOverlay={false}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} loading={saving}>Record payment</Button>
        </>
      }
    >
      {invoice && (
        <div className="bl-form-stack">
          <p className="bl-muted">
            {invoice.clientName} · Total {formatINR(invoice.totalAmount)} · Balance{' '}
            <strong>{formatINR(invoice.balanceAmount)}</strong>
          </p>
          <div className="bl-form-grid">
            <CmsField
              id="pay-amount"
              label="Amount (₹)"
              required
              value={form.amount}
              onChange={(v) => setField('amount', v)}
              error={errors.amount}
              inputProps={{ inputMode: 'decimal' }}
            />
            <CmsField
              id="pay-date"
              type="date"
              label="Payment date"
              required
              value={form.paymentDate}
              onChange={(v) => setField('paymentDate', v)}
              error={errors.paymentDate}
              inputProps={{ max: todayISO() }}
            />
            <CmsField
              id="pay-mode"
              type="select"
              label="Mode"
              options={PAYMENT_MODES}
              value={form.mode}
              onChange={(v) => setField('mode', v)}
              error={errors.mode}
            />
            <CmsField
              id="pay-ref"
              label="Reference"
              placeholder="UTR / cheque no."
              value={form.reference}
              onChange={(v) => setField('reference', v)}
              error={errors.reference}
            />
            <div className="bl-span-all">
              <CmsField
                id="pay-notes"
                type="textarea"
                label="Notes"
                rows={2}
                value={form.notes}
                onChange={(v) => setField('notes', v)}
                error={errors.notes}
              />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default RecordPaymentModal;
